import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

const BASE_URL = '/api/events';

// Events
export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.event_type) queryParams.append('event_type', params.event_type);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.target_audience) queryParams.append('target_audience', params.target_audience);
      // Only append is_published if it's explicitly set to 'true' or 'false' (not empty string)
      if (params.is_published === 'true' || params.is_published === 'false') {
        queryParams.append('is_published', params.is_published);
      }
      // Only append is_cancelled if it's explicitly set to 'true' or 'false' (not empty string)
      if (params.is_cancelled === 'true' || params.is_cancelled === 'false') {
        queryParams.append('is_cancelled', params.is_cancelled);
      }
      if (params.search) queryParams.append('search', params.search);
      if (params.class_id) queryParams.append('class_id', params.class_id);
      
      const response = await axiosInstance.get(`${BASE_URL}/events/?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEvent = createAsyncThunk(
  'events/fetchEvent',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/events/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/events/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/events/${id}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/events/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const publishEvent = createAsyncThunk(
  'events/publishEvent',
  async ({ id, is_published }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/events/${id}/publish/`, { is_published });
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelEvent = createAsyncThunk(
  'events/cancelEvent',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`${BASE_URL}/events/${id}/cancel/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const duplicateEvent = createAsyncThunk(
  'events/duplicateEvent',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/events/${id}/duplicate/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEventCostsSummary = createAsyncThunk(
  'events/fetchEventCostsSummary',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/events/${id}/get_costs_summary/`);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEventRegistrations = createAsyncThunk(
  'events/fetchEventRegistrations',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/events/${id}/get_registrations/`);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCalendarEvents = createAsyncThunk(
  'events/fetchCalendarEvents',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.month) queryParams.append('month', params.month);
      if (params.year) queryParams.append('year', params.year);
      
      const response = await axiosInstance.get(`${BASE_URL}/events/get_calendar/?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Event Costs
export const fetchEventCosts = createAsyncThunk(
  'events/fetchEventCosts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.event) queryParams.append('event', params.event);
      if (params.cost_type) queryParams.append('cost_type', params.cost_type);
      if (params.is_paid !== undefined) queryParams.append('is_paid', params.is_paid);
      
      const response = await axiosInstance.get(`${BASE_URL}/costs/?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createEventCost = createAsyncThunk(
  'events/createEventCost',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/costs/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateEventCost = createAsyncThunk(
  'events/updateEventCost',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/costs/${id}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteEventCost = createAsyncThunk(
  'events/deleteEventCost',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/costs/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markCostAsPaid = createAsyncThunk(
  'events/markCostAsPaid',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/costs/${id}/mark_as_paid/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const linkCostToPayment = createAsyncThunk(
  'events/linkCostToPayment',
  async ({ id, payment_id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/costs/${id}/link_payment/`, { payment_id });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Event Registrations
export const fetchEventRegistrationsList = createAsyncThunk(
  'events/fetchEventRegistrationsList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.event) queryParams.append('event', params.event);
      if (params.student) queryParams.append('student', params.student);
      if (params.status) queryParams.append('status', params.status);
      
      const response = await axiosInstance.get(`${BASE_URL}/registrations/?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const registerForEvent = createAsyncThunk(
  'events/registerForEvent',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/registrations/register/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelRegistration = createAsyncThunk(
  'events/cancelRegistration',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/registrations/${id}/cancel_registration/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markAttended = createAsyncThunk(
  'events/markAttended',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/registrations/${id}/mark_attended/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const eventsSlice = createSlice({
  name: 'events',
  initialState: {
    events: [],
    currentEvent: null,
    calendarEvents: [],
    costs: [],
    registrations: [],
    costsSummary: null,
    loading: false,
    costsLoading: false,
    registrationsLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
    },
    clearCostsSummary: (state) => {
      state.costsSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Events
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchEvent.fulfilled, (state, action) => {
        state.currentEvent = action.payload;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        const index = state.events.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        if (state.currentEvent && state.currentEvent.id === action.payload.id) {
          state.currentEvent = action.payload;
        }
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.events = state.events.filter(e => e.id !== action.payload);
        if (state.currentEvent && state.currentEvent.id === action.payload) {
          state.currentEvent = null;
        }
      })
      .addCase(publishEvent.fulfilled, (state, action) => {
        const index = state.events.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.events[index].is_published = action.payload.is_published;
        }
      })
      .addCase(cancelEvent.fulfilled, (state, action) => {
        const index = state.events.findIndex(e => e.id === action.payload);
        if (index !== -1) {
          state.events[index].is_cancelled = true;
        }
      })
      .addCase(duplicateEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        state.calendarEvents = action.payload;
      })
      .addCase(fetchEventCostsSummary.fulfilled, (state, action) => {
        state.costsSummary = action.payload;
      })
      .addCase(fetchEventRegistrations.fulfilled, (state, action) => {
        state.registrations = action.payload.registrations || [];
      })
      // Costs
      .addCase(fetchEventCosts.pending, (state) => {
        state.costsLoading = true;
      })
      .addCase(fetchEventCosts.fulfilled, (state, action) => {
        state.costsLoading = false;
        state.costs = action.payload;
      })
      .addCase(fetchEventCosts.rejected, (state, action) => {
        state.costsLoading = false;
        state.error = action.payload;
      })
      .addCase(createEventCost.fulfilled, (state, action) => {
        state.costs.push(action.payload);
      })
      .addCase(updateEventCost.fulfilled, (state, action) => {
        const index = state.costs.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.costs[index] = action.payload;
        }
      })
      .addCase(deleteEventCost.fulfilled, (state, action) => {
        state.costs = state.costs.filter(c => c.id !== action.payload);
      })
      .addCase(markCostAsPaid.fulfilled, (state, action) => {
        const index = state.costs.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.costs[index] = action.payload;
        }
      })
      // Registrations
      .addCase(fetchEventRegistrationsList.pending, (state) => {
        state.registrationsLoading = true;
      })
      .addCase(fetchEventRegistrationsList.fulfilled, (state, action) => {
        state.registrationsLoading = false;
        state.registrations = action.payload;
      })
      .addCase(fetchEventRegistrationsList.rejected, (state, action) => {
        state.registrationsLoading = false;
        state.error = action.payload;
      })
      .addCase(registerForEvent.fulfilled, (state, action) => {
        state.registrations.push(action.payload);
      })
      .addCase(cancelRegistration.fulfilled, (state, action) => {
        const index = state.registrations.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.registrations[index] = action.payload;
        }
      })
      .addCase(markAttended.fulfilled, (state, action) => {
        const index = state.registrations.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.registrations[index] = action.payload;
        }
      });
  },
});

export const { clearError, clearCurrentEvent, clearCostsSummary } = eventsSlice.actions;
export default eventsSlice.reducer;
